import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from '@simplywallst/ui-core';
import { find, get, max, maxBy, min, minBy, now } from 'lodash';
import { useTracking } from 'react-tracking';
import { scaleLinear, scaleTime } from '@visx/scale';
import { LinePath } from '@visx/shape';
import { bisector } from 'd3-array';
import { Chart, Status, Wrapper, ChartWrapper } from './styled';
import { Tooltip } from './Tooltip';
import { BottomAxis } from './BottomAxis';
import { PEChart } from './PEChart';
import useWindowSize from '@/hooks/useWindowSize';
import { useDebounce } from '@simplywallst/hooks/useDebounce';
import useIsomorphicLayoutEffect from '@/hooks/useIsomorphicLayoutEffect';
import type { ChartData, PeriodType } from '../../hooks/useMarketPerformance';
import useReduxStateMarket from '@/pages/Market/hooks/useReduxStateMarkets';
import PerformanceChartHeader from './PerformanceChartHeader';
import type { QueryStatus } from '@tanstack/react-query';

export const verticalPaddingSmall = 6;
export const verticalPaddingLarge = 16;
export const horizontalPadding = 6;
export const peChartHeight = 115;
export const chartHeight = 380;
export const buttonHeight = 32;
export const buttonWidth = 100;
export const bisectDate = bisector((d) => d).left;
export const lineChartPadding = 8;
export const PETooltipWidth = 296;

const defaultChartData = {
  time: now(),
  dateFormatted: '',
  medianPE: 1,
  medianPEFormatted: '',
  ps: 1,
  psFormatted: '',
  absolutePE: 1,
  absolutePEFormatted: '',
  earnings: 1,
  earningsDiff: 1,
  earningsFormatted: '',
  revenue: 1,
  revenueDiff: 1,
  revenueFormatted: '',
  marketCap: 1,
  marketCapDiff: 1,
  marketCapFormatted: '',
};

export const ValuationOptions = [
  {
    label: 'Price to Earnings',
    option: 'absolutePE',
  },
  { label: 'Price to Sales', option: 'ps' },
  { label: 'Median Price to Earnings', option: 'medianPE' },
];

export type ValuationType = 'medianPE' | 'absolutePE' | 'ps';

interface Props {
  chartData: ChartData;
  averages: Record<ValuationType, Record<PeriodType, number>>;
  period: PeriodType;
  status: QueryStatus;
  isShowingTable: boolean;
  handlePeriodSelect: (period: string | number) => void;
}

export const MarketPerformanceChart = ({
  chartData,
  averages,
  period,
  isShowingTable,
  status,
  handlePeriodSelect,
}: Props) => {
  const [valuationType, setValuationType] =
    useState<ValuationType>('absolutePE');
  const [width, setWidth] = useState<number>(0);
  const [index, setIndex] = useState<number>(0);
  const chartRef = useRef<HTMLDivElement>(null);

  const theme = useTheme();
  const debouncedSize = useDebounce(useWindowSize(), 1000);
  const { trackEvent } = useTracking();
  const { market, sector, secondary, tertiary } = useReduxStateMarket();

  const averageValuation = averages[valuationType][period];
  const verticalPadding =
    width > 480 ? verticalPaddingLarge : verticalPaddingSmall;
  const valuationLabel = get(
    find(ValuationOptions, ['option', valuationType]),
    'label',
    ''
  );

  useEffect(() => {
    if (chartData.length > 0) {
      const latestData = chartData[chartData.length - 1];
      const currentAbsolutePE = latestData.absolutePE;
      if (currentAbsolutePE < 0) {
        setValuationType('ps');
      }
    }
  }, [market, sector, secondary, tertiary, chartData]);

  useEffect(() => {
    if (chartData.length > 0) {
      setIndex(chartData.length - 1);
    }
  }, [chartData]);

  useIsomorphicLayoutEffect(() => {
    const { current } = chartRef;

    if (current) {
      const nextWidth = current.getBoundingClientRect().width;
      if (nextWidth > 0 && nextWidth !== width) {
        setWidth(nextWidth);
      }
    }
  }, [debouncedSize, width]);

  const handleValuationTypeSelect = useCallback(
    (value) => {
      setValuationType(value);
      trackEvent({
        type: 'track',
        action: 'click',
        subject: 'market-performance-valuation-type-selection',
        data: value,
      });
    },
    [trackEvent]
  );

  const getValueByTime = useCallback(
    (time: number, key: string) => {
      const rs = find(chartData, ['time', time]);
      if (rs) {
        return rs[key];
      }
      return 1;
    },
    [chartData]
  );

  // Chart data calculations
  const keys = useMemo(() => chartData.map((a) => a.time), [chartData]);
  const datum = useMemo<ChartData[number]>(() => {
    const datum = chartData[index];
    if (datum) return datum;
    return defaultChartData;
  }, [index, chartData]);
  const range = useMemo(() => {
    return {
      peMax: get(maxBy(chartData, valuationType), valuationType, 0),
      peMin: get(minBy(chartData, valuationType), valuationType, 0),
      timeMax: get(maxBy(chartData, 'time'), 'time', 0),
      timeMin: get(minBy(chartData, 'time'), 'time', 0),
      valuationMax:
        max([
          ...chartData.map((d) => d.earnings),
          ...chartData.map((d) => d.marketCap),
          ...chartData.map((d) => d.revenue),
        ]) || 0,
      valuationDiffMax:
        max([
          ...chartData.map((d) => d.earningsDiff),
          ...chartData.map((d) => d.marketCapDiff),
          ...chartData.map((d) => d.revenueDiff),
        ]) || 0,
      valuationDiffMin:
        min([
          ...chartData.map((d) => d.earningsDiff),
          ...chartData.map((d) => d.marketCapDiff),
          ...chartData.map((d) => d.revenueDiff),
        ]) || 0,
    };
  }, [chartData, valuationType]);

  // Scaling functions by x and y-axis
  const xScale = scaleTime<number>({
    range: [
      verticalPadding + lineChartPadding,
      width - (verticalPadding + lineChartPadding),
    ],
    domain: [range.timeMin, range.timeMax],
  });

  const xAxisScale = scaleTime<number>({
    range: [
      verticalPadding + lineChartPadding,
      width - (verticalPadding + lineChartPadding * 2),
    ],
    domain: [range.timeMin, range.timeMax],
  });

  const yScalePE = scaleLinear<number>({
    range: [
      horizontalPadding + lineChartPadding,
      peChartHeight - lineChartPadding,
    ],
    domain: [range.peMax, range.peMin],
  });

  const yScaleValuationRelative = scaleLinear<number>({
    range: [peChartHeight + buttonHeight + horizontalPadding * 4, chartHeight],
    domain: [range.valuationDiffMax, range.valuationDiffMin],
  });

  return (
    <ChartWrapper>
      <PerformanceChartHeader
        xPoint={xScale(keys[index])}
        isLoading={status === 'pending'}
        width={width}
        handlePeriodSelect={handlePeriodSelect}
        period={period}
        datum={datum}
        handleValuationTypeSelect={handleValuationTypeSelect}
        valuationType={valuationType}
        verticalPadding={verticalPadding}
        valuationLabel={valuationLabel}
      />
      <Wrapper ref={chartRef}>
        {status === 'pending' && <Status>Loading</Status>}
        {status === 'success' && keys.length === 0 && <Status>No data</Status>}
        {status === 'success' && keys.length > 0 && (
          <>
            <Tooltip
              width={width}
              keys={keys}
              chartData={chartData}
              height={chartHeight + 52}
              xScale={xScale}
              yScale={yScalePE}
              yScaleValuationRelative={yScaleValuationRelative}
              index={index}
              setIndex={setIndex}
              period={period}
              datum={datum}
              maxValuation={range.valuationMax}
              peMean={averageValuation}
              isShowingDots={!isShowingTable}
              valuationType={valuationType}
              verticalPadding={verticalPadding}
            />
            <Chart
              x={0}
              y={10}
              width={width}
              height={chartHeight + 100}
              data-cy-id="market-performance-chart"
            >
              <PEChart
                goodColor={theme.color.good}
                badColor={theme.color.bad}
                width={width}
                keys={keys}
                index={index}
                color={theme.color.neutral}
                xScale={xScale}
                yScale={yScalePE}
                getValueByTime={getValueByTime}
                mean={averageValuation}
                period={period}
                valuationType={valuationType}
                valuationLabel={valuationLabel as string}
              />
              <LinePath
                data={keys}
                stroke={theme.color.chart06}
                strokeWidth={2}
                strokeLinecap="round"
                x={(d) => xScale(d || 0) as number}
                y={(d) =>
                  yScaleValuationRelative(
                    getValueByTime(d, 'marketCapDiff') || 0
                  ) as number
                }
              />
              <LinePath
                data={keys}
                stroke={theme.color.chart02}
                strokeWidth={2}
                strokeLinecap="round"
                x={(d) => xScale(d || 0) as number}
                y={(d) =>
                  yScaleValuationRelative(
                    getValueByTime(d, 'earningsDiff') || 0
                  ) as number
                }
              />
              <LinePath
                data={keys}
                stroke={theme.color.chart01}
                strokeWidth={2}
                strokeLinecap="round"
                x={(d) => xScale(d || 0) as number}
                y={(d) =>
                  yScaleValuationRelative(
                    getValueByTime(d, 'revenueDiff') || 0
                  ) as number
                }
              />
              <BottomAxis
                xScale={xAxisScale}
                numTicks={4}
                top={chartHeight + 20}
                left={verticalPadding}
              />
            </Chart>
          </>
        )}
      </Wrapper>
    </ChartWrapper>
  );
};
